import React, { FunctionComponent, useEffect, useState, useMemo } from "react"
import { ColumnRowCell, ColumnHeaderCell } from "./Models"
import Pagination from "../Pagination"
import ColumnHeader from "./ColumnHeader"
import Row from "./Row"

interface TableProps {
	columns: ColumnHeaderCell[]
	rows: ColumnRowCell[][]
	hideFooter?: boolean
	expandableRow?: any
	pageSize?: number
}

const Table: FunctionComponent<TableProps> = ({ columns, rows, hideFooter, pageSize = 10 }) => {
	const [currentPage, setCurrentPage] = useState(1)
	const [tableRows, setTableRows] = useState(rows)
	const [sortedAscending, setSortedAscending] = useState<boolean>(true)
	const [sorted, setSorted] = useState(false)
	const [sortedColumn, setSortedColumn] = useState("")
	const [pSize, setPSize] = useState(pageSize)

	const currentTableData = useMemo(() => {
		const firstPageIndex = (currentPage - 1) * pSize
		const lastPageIndex = firstPageIndex + pSize
		return tableRows.slice(firstPageIndex, lastPageIndex)
	}, [currentPage, tableRows])

	useEffect(() => {
		setTableRows(rows)
		// goto page 1 on rerender
		setCurrentPage(1)
		setPSize(pageSize)
	}, [rows, pageSize])

	const convertToComparableValue = (value: any) => {
		if (typeof value === "string") {
			return value.toUpperCase()
		}
		return value
	}

	const sortByColumn = (column: string) => {
		const updatedRows = [...rows].sort((rowA, rowB) => {
			const colIdx = rowA.findIndex(row => row.columnKey === column)
			const valA = convertToComparableValue(rowA[colIdx].value)
			const valB = convertToComparableValue(rowB[colIdx].value)
			if (valA === null && valB !== null) {
				return sortedAscending ? -1 : 1
			}
			if (valA !== null && valB === null) {
				return sortedAscending ? 1 : -1
			}
			if (valA === null && valB === null) {
				return 0
			}
			if (sortedAscending) {
				if (typeof valA === "number") return valA - valB
				return valA.localeCompare(valB)
			} else {
				if (typeof valB === "number") return valB - valA
				return valB.localeCompare(valA)
			}
		})

		setSortedAscending(!sortedAscending)
		setTableRows(updatedRows)
		setSorted(true)
		setSortedColumn(column)
	}

	return (
		<>
			<div className='table-general-wrapper'>
				<table className='table-wrapper-container'>
					<thead className='table-head'>
						<tr className='table-column-container'>
							{columns.map(col => (
								<ColumnHeader
									col={col}
									sortedAscending={sortedAscending}
									sorted={sorted}
									sortByColumn={sortByColumn}
									sortedColumn={sortedColumn}
									key={col.columnKey}
								/>
							))}
						</tr>
					</thead>
					<tbody className='table-body-container'>
						{currentTableData.map((row, idx) => (
							<tr key={idx} className='table-row-wrap'>
								{row.map(el => (
									<Row value={el.displayValue} customClass={el.customClass} key={el.columnKey} url={el.url}/>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{hideFooter === true || rows.length <= pSize ? null : (
				<Pagination
					className='pagination-bar'
					currentPage={currentPage}
					totalCount={rows.length}
					pageSize={pSize}
					onPageChange={page => setCurrentPage(page)}
				/>
			)}
		</>
	)
}

export default Table